// InventoryPage.js
import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Container,
  Grid,
  Avatar,
  IconButton,
  Tooltip,
  Fab,
  Alert,
  AlertTitle,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Skeleton,
  useTheme,
  useMediaQuery,
  Button,
  Collapse,
} from "@mui/material";
import {
  Inventory,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Error,
  Refresh,
  Add,
  Analytics,
  Notifications,
  Settings,
  Search,
  FilterList,
  ViewModule,
  ViewList,
  KeyboardArrowUp,
  Restaurant,
  Assessment,
  LocalDining,
  Kitchen,
  RestaurantMenu,
  Grain,
  Liquor,
  Fastfood,
  EmojiFoodBeverage,
  LocalBar,
  ExpandMore,
  ExpandLess,
  Download,
  Share,
  Print,
  Visibility,
  AutoAwesome,
  Celebration,
  EmojiEvents,
  Storage,
  Inventory2,
  Scale,
  ProductionQuantityLimits,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { gql, useQuery } from "@apollo/client";
import Preloader from "../Components/Preloader/Preloader";
import InventoryCard from "../Components/inventory/InventoryCard";
import { useInventory } from "../Context/MyInventoryContext";
import { useKitchenDetail } from "../Context/GetMyKitchenDetailContext";
import { useGetMe } from "../Context/GetMeContext";
import BreadCrumb from "../Components/BreadCrumb";

// GraphQL query to get all box details for comprehensive insights
const GET_ALL_BOXES = gql`
  query GetAllBoxes($kitchen_id: ID!, $user_id: ID!) {
    getAllPantryBoxes: getInventoryTypes {
      inventory_type
      boxes: getBoxes(
        kitchen_id: $kitchen_id
        user_id: $user_id
        inventory_type: $inventory_type
      ) {
        id
        inventory_type
        box_no
        item_name
        box_capacity
        box_filled
        box_unit
        created
        modified
      }
    }
  }
`;

// Alternative query using individual calls for each pantry type
const GET_PANTRY_INSIGHTS = gql`
  query GetPantryInsights($kitchen_id: ID!, $user_id: ID!) {
    grainBoxes: getBoxes(
      kitchen_id: $kitchen_id
      user_id: $user_id
      inventory_type: GRAIN
    ) {
      id
      inventory_type
      box_no
      item_name
      box_capacity
      box_filled
      box_unit
      created
      modified
    }
    spiceBoxes: getBoxes(
      kitchen_id: $kitchen_id
      user_id: $user_id
      inventory_type: SPICE
    ) {
      id
      inventory_type
      box_no
      item_name
      box_capacity
      box_filled
      box_unit
      created
      modified
    }
    oilBoxes: getBoxes(
      kitchen_id: $kitchen_id
      user_id: $user_id
      inventory_type: OIL
    ) {
      id
      inventory_type
      box_no
      item_name
      box_capacity
      box_filled
      box_unit
      created
      modified
    }
    pasteBoxes: getBoxes(
      kitchen_id: $kitchen_id
      user_id: $user_id
      inventory_type: PASTE
    ) {
      id
      inventory_type
      box_no
      item_name
      box_capacity
      box_filled
      box_unit
      created
      modified
    }
    bowlBoxes: getBoxes(
      kitchen_id: $kitchen_id
      user_id: $user_id
      inventory_type: MANUALBOWL
    ) {
      id
      inventory_type
      box_no
      item_name
      box_capacity
      box_filled
      box_unit
      created
      modified
    }
    cupBoxes: getBoxes(
      kitchen_id: $kitchen_id
      user_id: $user_id
      inventory_type: MANUALCUP
    ) {
      id
      inventory_type
      box_no
      item_name
      box_capacity
      box_filled
      box_unit
      created
      modified
    }
  }
`;

const imageUrls = {
  GRAIN: "./images/grain.jpg",
  SPICE: "./images/spice.jpg",
  OIL: "./images/oil.jpg",
  PASTE: "./images/paste.png",
  MANUALBOWL: "./images/bowl.jpg",
  MANUALCUP: "./images/cup.jpg",
};

// Enhanced pantry configurations
const pantryConfig = {
  GRAIN: {
    icon: Grain,
    color: "#8bc34a",
    gradient: "linear-gradient(135deg, #8bc34a, #689f38)",
    category: "Dry Goods",
    title: "Grains",
    description: "Essential grains and cereals for your kitchen",
    bgPattern: "🌾",
    // Realistic thresholds based on typical pantry counts
    goodThreshold: 10, // 10+ items = good stock
    lowThreshold: 5, // 3-9 items = low stock
    criticalThreshold: 2, // 1-2 items = critical
    // 0 items = out of stock
    maxCapacity: 20, // Assume max capacity for percentage calculation
  },
  SPICE: {
    icon: Fastfood,
    color: "#ff9800",
    gradient: "linear-gradient(135deg, #ff9800, #f57c00)",
    category: "Seasonings",
    title: "Spices",
    description: "Aromatic spices and seasonings",
    bgPattern: "🌶️",
    goodThreshold: 8,
    lowThreshold: 4,
    criticalThreshold: 2,
    maxCapacity: 15,
  },
  OIL: {
    icon: Liquor,
    color: "#ffc107",
    gradient: "linear-gradient(135deg, #ffc107, #ff8f00)",
    category: "Liquids",
    title: "Glass Bottles",
    description: "Cooking oils and liquid ingredients",
    bgPattern: "🛢️",
    goodThreshold: 6,
    lowThreshold: 3,
    criticalThreshold: 1,
    maxCapacity: 12,
  },
  PASTE: {
    icon: LocalBar,
    color: "#e91e63",
    gradient: "linear-gradient(135deg, #e91e63, #c2185b)",
    category: "Sauces",
    title: "Sauce Bottles",
    description: "Sauces, pastes and condiments",
    bgPattern: "🍯",
    goodThreshold: 8,
    lowThreshold: 4,
    criticalThreshold: 2,
    maxCapacity: 15,
  },
  MANUALBOWL: {
    icon: EmojiFoodBeverage,
    color: "#9c27b0",
    gradient: "linear-gradient(135deg, #9c27b0, #7b1fa2)",
    category: "Utensils",
    title: "Bowls",
    description: "Serving bowls and containers",
    bgPattern: "🥣",
    // Realistic thresholds based on typical pantry counts
    goodThreshold: 15,
    lowThreshold: 8,
    criticalThreshold: 4,
    maxCapacity: 30,
    unit: "g", // Changed from pieces to g
  },
  MANUALCUP: {
    icon: LocalDining,
    color: "#3f51b5",
    gradient: "linear-gradient(135deg, #3f51b5, #303f9f)",
    category: "Utensils",
    title: "Cups",
    description: "Cups and drinking vessels",
    bgPattern: "☕",
    goodThreshold: 20,
    lowThreshold: 10,
    criticalThreshold: 5,
    maxCapacity: 40,
    unit: "g", // Changed from pieces to g
  },
};

// Box status helper function - for individual boxes with capacity/filled data
const getBoxStatus = (boxFilled, boxCapacity, pantryType) => {
  if (!boxCapacity || boxCapacity === 0) {
    return {
      status: "UNKNOWN",
      color: "#9e9e9e",
      label: "Unknown",
      icon: Error,
      severity: "info",
      percentage: 0,
    };
  }

  const percentage = (boxFilled / boxCapacity) * 100;

  if (percentage === 0) {
    return {
      status: "EMPTY",
      color: "#f44336",
      label: "Empty",
      icon: Error,
      severity: "error",
      percentage,
    };
  } else if (percentage <= 15) {
    // Less than 15% filled
    return {
      status: "CRITICAL",
      color: "#ff5722",
      label: "Critical Low",
      icon: Warning,
      severity: "error",
      percentage,
    };
  } else if (percentage <= 35) {
    // 15-35% filled
    return {
      status: "LOW",
      color: "#ff9800",
      label: "Running Low",
      icon: TrendingDown,
      severity: "warning",
      percentage,
    };
  } else {
    // More than 35% filled
    return {
      status: "GOOD",
      color: "#4caf50",
      label: "Well Stocked",
      icon: CheckCircle,
      severity: "success",
      percentage,
    };
  }
};

// Status helper function for pantry count - for basic pantry counts
const getPantryStatus = (count, type) => {
  const config = pantryConfig[type] || pantryConfig.GRAIN;

  if (count === 0) {
    return {
      status: "OUT_OF_STOCK",
      color: "#f44336",
      label: "Out of Stock",
      icon: Error,
      severity: "error",
      message: "Immediate restocking required",
      percentage: 0,
    };
  } else if (count <= config.criticalThreshold) {
    return {
      status: "CRITICAL",
      color: "#ff5722",
      label: "Critical Low",
      icon: Warning,
      severity: "error",
      message: "Urgent restocking needed",
      percentage: (count / config.maxCapacity) * 100,
    };
  } else if (count <= config.lowThreshold) {
    return {
      status: "LOW",
      color: "#ff9800",
      label: "Running Low",
      icon: TrendingDown,
      severity: "warning",
      message: "Consider restocking soon",
      percentage: (count / config.maxCapacity) * 100,
    };
  } else {
    return {
      status: "GOOD",
      color: "#4caf50",
      label: "Well Stocked",
      icon: CheckCircle,
      severity: "success",
      message: "Pantry levels are healthy",
      percentage: Math.min((count / config.maxCapacity) * 100, 100),
    };
  }
};

// Loading Skeleton Components
const PantryCardSkeleton = () => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        height: "100%",
        minHeight: 320,
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[900]}90 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[50]}90 100%)`,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Skeleton variant="rectangular" height={160} />
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width="70%" height={24} />
        </Box>
        <Skeleton variant="text" width="50%" height={16} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="40%" height={16} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" width="100%" height={6} sx={{ mb: 1 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Skeleton variant="text" width="30%" height={12} />
          <Skeleton variant="text" width="20%" height={12} />
        </Box>
        <Skeleton variant="rounded" width="80%" height={24} />
      </CardContent>
    </Card>
  );
};

const StatCardSkeleton = () => {
  const theme = useTheme();

  return (
    <Paper
      elevation={4}
      sx={{
        p: 3,
        borderRadius: 4,
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
        border: `1px solid ${theme.palette.divider}`,
        height: 120, // Fixed height for consistency
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, height: "100%" }}
      >
        <Skeleton variant="circular" width={56} height={56} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={16} />
        </Box>
        <Skeleton variant="circular" width={32} height={32} />
      </Box>
    </Paper>
  );
};

// Enhanced Statistics Card Component with fixed sizing
const StatisticsCard = ({
  icon: IconComponent,
  label,
  value,
  color,
  gradient,
  trend,
  suffix = "",
}) => {
  const theme = useTheme();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
      style={{ height: "100%" }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 3,
          borderRadius: 4,
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
              : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
          position: "relative",
          overflow: "hidden",
          transition: "all 0.3s ease",
          height: 120, // Fixed height for all cards
          display: "flex",
          alignItems: "center",
          "&:hover": {
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 12px 40px rgba(0,0,0,0.3)`
                : `0 12px 40px rgba(0,0,0,0.12)`,
            transform: "translateY(-2px)",
          },
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: `${color}10`,
            opacity: 0.3,
          }}
        />

        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          >
            <Avatar
              sx={{
                width: 56,
                height: 56,
                background: gradient,
                boxShadow: `0 4px 20px ${color}30`,
                flexShrink: 0,
              }}
            >
              <IconComponent sx={{ fontSize: 28, color: "white" }} />
            </Avatar>
          </motion.div>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {" "}
            {/* minWidth: 0 prevents flex item from growing beyond container */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: "1.75rem", // Consistent font size
                color: theme.palette.text.primary,
                lineHeight: 1,
                mb: 0.5,
                whiteSpace: "nowrap", // Prevent text wrapping
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {value}
              {suffix}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {label}
            </Typography>
          </Box>

          {trend !== undefined && (
            <motion.div
              animate={{ y: [-2, 2, -2] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: trend > 0 ? "#4caf5020" : "#f4433620",
                  flexShrink: 0,
                }}
              >
                {trend > 0 ? (
                  <TrendingUp sx={{ fontSize: 16, color: "#4caf50" }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 16, color: "#f44336" }} />
                )}
              </Avatar>
            </motion.div>
          )}
        </Box>
      </Paper>
    </motion.div>
  );
};

const PantryPage = () => {
  const { loading, inventory, refetch } = useInventory();
  const { kitchenDetail } = useKitchenDetail();
  const { user } = useGetMe();
  const userdata = user?.me;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showStatistics, setShowStatistics] = useState(true);
  const [alertsExpanded, setAlertsExpanded] = useState(true);

  // Fetch comprehensive box insights
  const {
    loading: insightsLoading,
    data: insightsData,
    error: insightsError,
    refetch: refetchInsights,
  } = useQuery(GET_PANTRY_INSIGHTS, {
    variables: {
      kitchen_id: kitchenDetail?.kitchenId,
      user_id: userdata?.id,
    },
    skip: !kitchenDetail?.kitchenId || !userdata?.id,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  // Calculate comprehensive statistics from box data
  const comprehensiveStatistics = useMemo(() => {
    if (!insightsData) return null;

    const allBoxes = [
      ...(insightsData.grainBoxes || []),
      ...(insightsData.spiceBoxes || []),
      ...(insightsData.oilBoxes || []),
      ...(insightsData.pasteBoxes || []),
      ...(insightsData.bowlBoxes || []),
      ...(insightsData.cupBoxes || []),
    ];

    if (!allBoxes.length) return null;

    const totalBoxes = allBoxes.length;
    const filledBoxes = allBoxes.filter((box) => box.box_filled > 0).length;
    const emptyBoxes = allBoxes.filter((box) => box.box_filled === 0).length;
    const namedBoxes = allBoxes.filter(
      (box) => box.item_name && box.item_name.trim()
    ).length;

    const totalCapacity = allBoxes.reduce(
      (sum, box) => sum + (box.box_capacity || 0),
      0
    );
    const totalFilled = allBoxes.reduce(
      (sum, box) => sum + (box.box_filled || 0),
      0
    );
    const utilizationPercentage =
      totalCapacity > 0 ? (totalFilled / totalCapacity) * 100 : 0;

    // Calculate status breakdown
    const statusCounts = allBoxes.reduce((acc, box) => {
      const status = getBoxStatus(
        box.box_filled,
        box.box_capacity,
        box.inventory_type
      );
      acc[status.severity] = (acc[status.severity] || 0) + 1;
      return acc;
    }, {});

    // Category breakdown with aggregate status calculation
    const categoryStats = Object.keys(pantryConfig).reduce((acc, type) => {
      const categoryBoxes = allBoxes.filter(
        (box) => box.inventory_type === type
      );
      const categoryCapacity = categoryBoxes.reduce(
        (sum, box) => sum + (box.box_capacity || 0),
        0
      );
      const categoryFilled = categoryBoxes.reduce(
        (sum, box) => sum + (box.box_filled || 0),
        0
      );

      // Calculate aggregate status for this category
      let aggregateStatus = "GOOD";
      let aggregatePercentage =
        categoryCapacity > 0 ? (categoryFilled / categoryCapacity) * 100 : 0;

      if (categoryBoxes.length === 0 || categoryFilled === 0) {
        aggregateStatus = "OUT_OF_STOCK";
        aggregatePercentage = 0;
      } else if (aggregatePercentage <= 15) {
        aggregateStatus = "CRITICAL";
      } else if (aggregatePercentage <= 35) {
        aggregateStatus = "LOW";
      } else {
        aggregateStatus = "GOOD";
      }

      acc[type] = {
        totalBoxes: categoryBoxes.length,
        filledBoxes: categoryBoxes.filter((box) => box.box_filled > 0).length,
        totalCapacity: categoryCapacity,
        totalFilled: categoryFilled,
        utilization: aggregatePercentage,
        aggregateStatus,
        boxes: categoryBoxes, // Include box details
      };
      return acc;
    }, {});

    return {
      totalBoxes,
      filledBoxes,
      emptyBoxes,
      namedBoxes,
      totalCapacity,
      totalFilled,
      utilizationPercentage,
      statusCounts,
      categoryStats,
      averageCapacity: totalCapacity / totalBoxes,
      averageFilled: totalFilled / totalBoxes,
    };
  }, [insightsData]);

  // Enhanced alerts from box data
  const enhancedAlerts = useMemo(() => {
    if (!insightsData) return [];

    const allBoxes = [
      ...(insightsData.grainBoxes || []),
      ...(insightsData.spiceBoxes || []),
      ...(insightsData.oilBoxes || []),
      ...(insightsData.pasteBoxes || []),
      ...(insightsData.bowlBoxes || []),
      ...(insightsData.cupBoxes || []),
    ];

    return allBoxes
      .map((box) => {
        const config = pantryConfig[box.inventory_type];
        const status = getBoxStatus(
          box.box_filled,
          box.box_capacity,
          box.inventory_type
        );
        return { ...box, config, status };
      })
      .filter(
        (box) =>
          box.status.severity === "error" || box.status.severity === "warning"
      )
      .sort((a, b) => {
        if (a.status.severity === "error" && b.status.severity === "warning")
          return -1;
        if (a.status.severity === "warning" && b.status.severity === "error")
          return 1;
        return 0;
      })
      .slice(0, 10); // Limit to top 10 most critical
  }, [insightsData]);

  // Get enhanced pantry data with box-level calculations
  const enhancedPantryData = useMemo(() => {
    if (!inventory?.length) return [];

    return inventory.map((item) => {
      const config = pantryConfig[item.inventoryType];

      // If we have comprehensive statistics, use the real box data
      if (comprehensiveStatistics?.categoryStats[item.inventoryType]) {
        const categoryData =
          comprehensiveStatistics.categoryStats[item.inventoryType];
        return {
          ...item,
          // Use actual box-based calculations instead of simple counts
          realCount: categoryData.filledBoxes, // Number of non-empty boxes
          totalBoxes: categoryData.totalBoxes,
          utilization: categoryData.utilization,
          aggregateStatus: categoryData.aggregateStatus,
          totalCapacity: categoryData.totalCapacity,
          totalFilled: categoryData.totalFilled,
          hasComprehensiveData: true,
          boxes: categoryData.boxes,
        };
      } else {
        // Fallback to basic count-based calculation
        const status = getPantryStatus(item.count, item.inventoryType);
        return {
          ...item,
          realCount: item.count,
          totalBoxes: item.count,
          utilization: status.percentage,
          aggregateStatus: status.status,
          hasComprehensiveData: false,
        };
      }
    });
  }, [inventory, comprehensiveStatistics]);

  // Calculate statistics from basic pantry (fallback)
  const basicStatistics = useMemo(() => {
    if (!inventory?.length) return null;

    const totalTypes = inventory.length;
    const totalItems = inventory.reduce(
      (sum, item) => sum + (item.count || 0),
      0
    );
    const lowStockItems = inventory.filter((item) => {
      const status = getPantryStatus(item.count, item.inventoryType);
      return status.severity === "warning" || status.severity === "error";
    }).length;
    const outOfStockItems = inventory.filter(
      (item) => (item.count || 0) === 0
    ).length;

    return { totalTypes, totalItems, lowStockItems, outOfStockItems };
  }, [inventory]);

  // Get alerts from basic pantry (fallback)
  const basicAlerts = useMemo(() => {
    if (!inventory?.length) return [];

    return inventory
      .map((item) => {
        const config = pantryConfig[item.inventoryType];
        const status = getPantryStatus(item.count, item.inventoryType);
        return {
          ...item,
          config,
          status,
        };
      })
      .filter(
        (item) =>
          item.status.severity === "error" || item.status.severity === "warning"
      )
      .sort((a, b) => {
        if (a.status.severity === "error" && b.status.severity === "warning")
          return -1;
        if (a.status.severity === "warning" && b.status.severity === "error")
          return 1;
        return 0;
      });
  }, [inventory]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([refetch(), refetchInsights()]);
    } catch (error) {
      console.error("Failed to refresh pantry:", error);
    } finally {
      setRefreshing(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Scroll management
  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (loading && !inventory) {
    return <Preloader />;
  }

  const statistics = comprehensiveStatistics || basicStatistics;
  const alerts = enhancedAlerts.length > 0 ? enhancedAlerts : basicAlerts;

  return (
    <>
      <BreadCrumb
        links={[{ label: "Dashboard", href: "/dashboard" }]}
        current="Pantry"
      />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Enhanced Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            elevation={12}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 4,
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, 
                  ${theme.palette.primary.dark}15 0%, 
                  ${theme.palette.background.paper} 20%,
                  ${theme.palette.background.paper} 80%,
                  ${theme.palette.secondary.dark}15 100%)`
                  : `linear-gradient(135deg, 
                  ${theme.palette.primary.light}15 0%, 
                  ${theme.palette.background.paper} 20%,
                  ${theme.palette.background.paper} 80%,
                  ${theme.palette.secondary.light}15 100%)`,
              border: `1px solid ${theme.palette.divider}`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Background Pattern */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.05,
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${theme.palette.primary.main.slice(
                  1
                )}' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                position: "relative",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                <motion.div
                  animate={{
                    rotate: [0, 5, -5, 0],
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    repeatDelay: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
                    }}
                  >
                    <Inventory sx={{ fontSize: 40, color: "white" }} />
                  </Avatar>
                </motion.div>
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                      background:
                        theme.palette.mode === "dark"
                          ? `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`
                          : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  >
                    Pantry Management
                  </Typography>
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.2rem" },
                      fontWeight: 500,
                    }}
                  >
                    Monitor and control your kitchen pantry
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1 }}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Tooltip title="Refresh Pantry">
                    <IconButton
                      onClick={handleRefresh}
                      disabled={refreshing}
                      sx={{
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        color: "white",
                        width: 56,
                        height: 56,
                        boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                        "&:hover": {
                          background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                          boxShadow: `0 12px 40px ${theme.palette.primary.main}60`,
                        },
                      }}
                    >
                      <motion.div
                        animate={refreshing ? { rotate: 360 } : {}}
                        transition={{
                          duration: 1,
                          repeat: refreshing ? Infinity : 0,
                        }}
                      >
                        <Refresh sx={{ fontSize: 28 }} />
                      </motion.div>
                    </IconButton>
                  </Tooltip>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Tooltip
                    title={`Switch to ${
                      viewMode === "grid" ? "List" : "Grid"
                    } View`}
                  >
                    <IconButton
                      onClick={() =>
                        setViewMode(viewMode === "grid" ? "list" : "grid")
                      }
                      sx={{
                        background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                        color: "white",
                        width: 56,
                        height: 56,
                        boxShadow: `0 4px 16px ${theme.palette.secondary.main}40`,
                        "&:hover": {
                          background: `linear-gradient(45deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.main})`,
                          boxShadow: `0 12px 40px ${theme.palette.secondary.main}60`,
                        },
                      }}
                    >
                      {viewMode === "grid" ? (
                        <ViewList sx={{ fontSize: 28 }} />
                      ) : (
                        <ViewModule sx={{ fontSize: 28 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </motion.div>
              </Box>
            </Box>
          </Paper>
        </motion.div>

        {/* Enhanced Statistics Section */}
        {statistics && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
              >
                <motion.div
                  animate={{ scale: [1, 1.1, 1], rotate: [0, 180, 360] }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                >
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.info.main})`,
                      boxShadow: `0 4px 20px ${theme.palette.success.main}30`,
                    }}
                  >
                    <Assessment sx={{ color: "white", fontSize: 28 }} />
                  </Avatar>
                </motion.div>
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{
                      background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.info.main})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      mb: 0.5,
                    }}
                  >
                    {comprehensiveStatistics
                      ? "Comprehensive Analytics"
                      : "Pantry Overview"}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ opacity: 0.8 }}
                  >
                    {comprehensiveStatistics
                      ? "Real-time box-level pantry insights and analytics"
                      : "Real-time pantry statistics and insights"}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setShowStatistics(!showStatistics)}
                  sx={{ ml: "auto" }}
                >
                  {showStatistics ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              <Collapse in={showStatistics}>
                {loading || insightsLoading ? (
                  <Grid container spacing={3}>
                    {Array.from({
                      length: comprehensiveStatistics ? 6 : 4,
                    }).map((_, index) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={comprehensiveStatistics ? 4 : 3}
                        lg={comprehensiveStatistics ? 2 : 3}
                        key={index}
                      >
                        <StatCardSkeleton />
                      </Grid>
                    ))}
                  </Grid>
                ) : comprehensiveStatistics ? (
                  // Enhanced statistics with box-level data - Fixed Grid Layout
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4} xl={2}>
                      <StatisticsCard
                        icon={Storage}
                        label="Total Boxes"
                        value={comprehensiveStatistics.totalBoxes}
                        color="#2196f3"
                        gradient="linear-gradient(135deg, #2196f3, #1976d2)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} xl={2}>
                      <StatisticsCard
                        icon={Inventory2}
                        label="Active Boxes"
                        value={comprehensiveStatistics.filledBoxes}
                        color="#4caf50"
                        gradient="linear-gradient(135deg, #4caf50, #388e3c)"
                        trend={1}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} xl={2}>
                      <StatisticsCard
                        icon={ProductionQuantityLimits}
                        label="Empty Boxes"
                        value={comprehensiveStatistics.emptyBoxes}
                        color="#f44336"
                        gradient="linear-gradient(135deg, #f44336, #d32f2f)"
                        trend={-1}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} xl={2}>
                      <StatisticsCard
                        icon={Scale}
                        label="Utilization"
                        value={Math.round(
                          comprehensiveStatistics.utilizationPercentage
                        )}
                        suffix="%"
                        color="#ff9800"
                        gradient="linear-gradient(135deg, #ff9800, #f57c00)"
                        trend={
                          comprehensiveStatistics.utilizationPercentage > 50
                            ? 1
                            : -1
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} xl={2}>
                      <StatisticsCard
                        icon={Assessment}
                        label="Total Capacity"
                        value={(() => {
                          // Get a mixed unit display for total capacity
                          const categoryStats =
                            comprehensiveStatistics.categoryStats;
                          let totalGrams = 0;
                          let totalMl = 0;

                          Object.keys(categoryStats).forEach((type) => {
                            const stats = categoryStats[type];
                            const config = pantryConfig[type];

                            if (
                              type === "GRAIN" ||
                              type === "SPICE" ||
                              type === "MANUALBOWL" ||
                              type === "MANUALCUP"
                            ) {
                              totalGrams += stats.totalCapacity;
                            } else if (type === "OIL" || type === "PASTE") {
                              totalMl += stats.totalCapacity;
                            }
                          });

                          // Return the largest value with appropriate unit
                          if (totalGrams >= totalMl) {
                            return totalGrams >= 1000
                              ? `${Math.round(totalGrams / 1000)}kg`
                              : `${Math.round(totalGrams)}g`;
                          } else {
                            return totalMl >= 1000
                              ? `${Math.round(totalMl / 1000)}L`
                              : `${Math.round(totalMl)}ml`;
                          }
                        })()}
                        color="#9c27b0"
                        gradient="linear-gradient(135deg, #9c27b0, #7b1fa2)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} xl={2}>
                      <StatisticsCard
                        icon={Kitchen}
                        label="Labeled Items"
                        value={comprehensiveStatistics.namedBoxes}
                        color="#00bcd4"
                        gradient="linear-gradient(135deg, #00bcd4, #0097a7)"
                      />
                    </Grid>
                  </Grid>
                ) : (
                  // Basic statistics fallback
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatisticsCard
                        icon={Restaurant}
                        label="Total Categories"
                        value={statistics.totalTypes}
                        color="#2196f3"
                        gradient="linear-gradient(135deg, #2196f3, #1976d2)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatisticsCard
                        icon={Assessment}
                        label="Total Items"
                        value={statistics.totalItems}
                        color="#4caf50"
                        gradient="linear-gradient(135deg, #4caf50, #388e3c)"
                        trend={1}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatisticsCard
                        icon={Warning}
                        label="Low Stock Items"
                        value={statistics.lowStockItems}
                        color="#ff9800"
                        gradient="linear-gradient(135deg, #ff9800, #f57c00)"
                        trend={-1}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StatisticsCard
                        icon={Error}
                        label="Out of Stock"
                        value={statistics.outOfStockItems}
                        color="#f44336"
                        gradient="linear-gradient(135deg, #f44336, #d32f2f)"
                        trend={-1}
                      />
                    </Grid>
                  </Grid>
                )}
              </Collapse>
            </Box>
          </motion.div>
        )}

        {/* Enhanced Alerts Section */}
        {alerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Paper
              elevation={6}
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 4,
                background:
                  theme.palette.mode === "dark"
                    ? `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[900]}50 100%)`
                    : `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[50]}50 100%)`,
                border: `1px solid ${theme.palette.divider}30`,
                backdropFilter: "blur(20px)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: "#ff980020",
                      border: `2px solid ${theme.palette.warning.main}30`,
                    }}
                  >
                    <Notifications
                      sx={{ color: theme.palette.warning.main, fontSize: 24 }}
                    />
                  </Avatar>
                </motion.div>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={700}>
                    {comprehensiveStatistics ? "Box Alerts" : "Pantry Alerts"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {alerts.length}{" "}
                    {comprehensiveStatistics ? "box(es)" : "item(s)"} require
                    attention
                  </Typography>
                </Box>
                <IconButton onClick={() => setAlertsExpanded(!alertsExpanded)}>
                  {alertsExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              <Collapse in={alertsExpanded}>
                <Stack spacing={2}>
                  {alerts.map((item, index) => (
                    <motion.div
                      key={
                        comprehensiveStatistics ? item.id : item.inventoryType
                      }
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Alert
                        severity={item.status.severity}
                        icon={<item.status.icon />}
                        sx={{
                          borderRadius: 3,
                          "& .MuiAlert-icon": {
                            fontSize: "1.5rem",
                          },
                        }}
                      >
                        <AlertTitle sx={{ fontWeight: 700 }}>
                          {comprehensiveStatistics ? (
                            <>
                              {item.config.title} - Box #{item.box_no}:{" "}
                              {item.item_name || "Unlabeled"} -{" "}
                              {item.status.label}
                            </>
                          ) : (
                            <>
                              {item.config.title} - {item.status.label}
                            </>
                          )}
                        </AlertTitle>
                        <Typography variant="body2">
                          {comprehensiveStatistics ? (
                            <>
                              <strong>
                                {Math.round(item.status.percentage)}%
                              </strong>{" "}
                              filled ({item.box_filled}/{item.box_capacity}{" "}
                              {item.box_unit}). {item.status.label}.
                            </>
                          ) : (
                            <>
                              Current stock: <strong>{item.count}</strong>{" "}
                              items. {item.status.message}
                            </>
                          )}
                        </Typography>
                      </Alert>
                    </motion.div>
                  ))}
                </Stack>
              </Collapse>
            </Paper>
          </motion.div>
        )}

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          {loading ? (
            <Grid container spacing={3}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Grid
                  item
                  xs={12}
                  sm={viewMode === "grid" ? 6 : 12}
                  md={viewMode === "grid" ? 4 : 12}
                  lg={viewMode === "grid" ? 4 : 12}
                  key={index}
                >
                  <PantryCardSkeleton />
                </Grid>
              ))}
            </Grid>
          ) : !inventory?.length ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={4}
                sx={{
                  p: 6,
                  borderRadius: 4,
                  textAlign: "center",
                  background:
                    theme.palette.mode === "dark"
                      ? `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.grey[900]})`
                      : `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.grey[50]})`,
                  border: `2px dashed ${theme.palette.divider}`,
                }}
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                >
                  <Inventory
                    sx={{
                      fontSize: 80,
                      color: theme.palette.text.secondary,
                      opacity: 0.5,
                      mb: 2,
                    }}
                  />
                </motion.div>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: 700, mb: 2 }}
                >
                  No Pantry Data Found
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  Unable to load pantry information at this time
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ opacity: 0.7 }}
                >
                  Please check your connection and try refreshing the page
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{
                    mt: 3,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    "&:hover": {
                      background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                    },
                  }}
                >
                  {refreshing ? "Refreshing..." : "Refresh Pantry"}
                </Button>
              </Paper>
            </motion.div>
          ) : (
            <Grid container spacing={3}>
              <AnimatePresence mode="wait">
                {enhancedPantryData.map((item, index) => {
                  const config = pantryConfig[item.inventoryType];
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={viewMode === "grid" ? 6 : 12}
                      md={viewMode === "grid" ? 4 : 12}
                      lg={viewMode === "grid" ? 4 : 12}
                      key={item.inventoryType}
                    >
                      <InventoryCard
                        title={config?.title || item.inventoryType}
                        description={`${item.realCount} ${
                          config?.category || "Items"
                        }`}
                        imageUrl={
                          imageUrls[item.inventoryType] ||
                          "./placeholder-16x9.jpg"
                        }
                        url={item.inventoryType}
                        count={item.realCount}
                        inventoryType={item.inventoryType}
                        loading={false}
                        utilization={item.utilization}
                        aggregateStatus={item.aggregateStatus}
                        totalBoxes={item.totalBoxes}
                        totalCapacity={item.totalCapacity}
                        totalFilled={item.totalFilled}
                        hasComprehensiveData={item.hasComprehensiveData}
                        boxes={item.boxes}
                      />
                    </Grid>
                  );
                })}
              </AnimatePresence>
            </Grid>
          )}
        </motion.div>

        {/* Enhanced Scroll to Top */}
        <AnimatePresence>
          {showScrollTop && (
            <motion.div
              initial={{ opacity: 0, scale: 0, rotate: -180 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0, rotate: 180 }}
              transition={{ duration: 0.3, type: "spring" }}
              style={{
                position: "fixed",
                bottom: 24,
                right: 24,
                zIndex: 1000,
              }}
            >
              <Tooltip title="Back to Top">
                <Fab
                  color="primary"
                  size="large"
                  onClick={scrollToTop}
                  sx={{
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
                    width: 64,
                    height: 64,
                    "&:hover": {
                      transform: "scale(1.1)",
                    },
                    transition: "transform 0.2s ease",
                  }}
                >
                  <motion.div
                    animate={{ y: [-2, 2, -2] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <KeyboardArrowUp sx={{ fontSize: 32, color: "white" }} />
                  </motion.div>
                </Fab>
              </Tooltip>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>
    </>
  );
};

export default React.memo(PantryPage);
